import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicationNotification, StatusChangeNotification } from '../model/application-notification';
import { MobileStorageManager } from './mobile-storage-manager';

export abstract class NotificationService {
    abstract list(): Observable<ApplicationNotification[]>;
    abstract create(notification: ApplicationNotification): Observable<void>;
    abstract markAsRead(notificationId: string): Observable<void>;
    abstract deleteStatusChangesForRequest(requestId: string): Observable<void>;
    abstract countUnread(): Observable<number>;
}

@Injectable()
export class NotificationServiceImpl extends NotificationService {
    constructor(private manager: MobileStorageManager) {
        super();
    }

    list(): Observable<ApplicationNotification[]> {
        return this.asObservable(() => {
            return this.manager.loadNotificationListFromStorage();
        });
    }

    create(notification: ApplicationNotification): Observable<void> {
        return this.asObservable(() => {
            this.manager.storeNotificationItemToStorage(notification);
        });
    }

    markAsRead(notificationId: string): Observable<void> {
        return this.asObservable(() => {
            let item = this.manager.loadNotificationItemFromStorage(notificationId);
            item.read = true;
            this.manager.storeNotificationItemToStorage(item);
        });
    }

    deleteStatusChangesForRequest(requestId: string): Observable<void> {
        return this.asObservable(() => {
            this.manager
                .loadNotificationListFromStorage()
                .filter((i) => i instanceof StatusChangeNotification)
                .filter((i) => (i as StatusChangeNotification).requestId === requestId)
                .forEach((i) => this.manager.removeNotificationFromStorage(i.id));
        });
    }

    countUnread(): Observable<number> {
        return this.list().pipe(map((list) => list.filter(({ read }) => !read).length));
    }

    private asObservable<T>(action: () => T): Observable<T> {
        return new Observable<T>((observer) => {
            try {
                observer.next(action());
                observer.complete();
            } catch (error: any) {
                observer.error(error);
            }
        });
    }
}
